import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { loginRequest, LoginPayload, logoutRequest } from '../api/auth';
import { USER_STORAGE_KEY, TOKEN_STORAGE_KEY, STORE_STORAGE_KEY } from '../config';
import { User } from '../types/api';
import { Store } from '../api/stores';

interface AuthContextValue {
  user: User | null;
  token: string | null;
  selectedStore: Store | null;
  isAuthenticating: boolean;
  login: (payload: LoginPayload) => Promise<void>;
  logout: () => Promise<void>;
  setSelectedStore: (store: Store | null) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem(USER_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as User) : null;
  });
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  });
  const [selectedStore, setSelectedStoreState] = useState<Store | null>(() => {
    const stored = localStorage.getItem(STORE_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as Store) : null;
  });
  const [isAuthenticating, setAuthenticating] = useState(false);

  const setSelectedStore = useCallback((store: Store | null) => {
    setSelectedStoreState(store);
    if (store) {
      localStorage.setItem(STORE_STORAGE_KEY, JSON.stringify(store));
    } else {
      localStorage.removeItem(STORE_STORAGE_KEY);
    }
  }, []);

  const login = useCallback(async (payload: LoginPayload) => {
    setAuthenticating(true);
    try {
      const data = await loginRequest(payload);
      setToken(data.access_token);
      setUser(data.user);
      localStorage.setItem(TOKEN_STORAGE_KEY, data.access_token);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
    } finally {
      setAuthenticating(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } catch (error) {
      // ignore logout API errors
    } finally {
      setToken(null);
      setUser(null);
      setSelectedStoreState(null);
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(USER_STORAGE_KEY);
      localStorage.removeItem(STORE_STORAGE_KEY);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      selectedStore,
      isAuthenticating,
      login,
      logout,
      setSelectedStore,
    }),
    [user, token, selectedStore, isAuthenticating, login, logout, setSelectedStore]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};

